import { Component, Input, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { BackendPushResponse, Datastore } from '@freelancer/datastore';
import { ViolationReportsCollection } from '@freelancer/datastore/collections';
import { ModalRef } from '@freelancer/ui';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { HeadingType } from '@freelancer/ui/heading';
import { HoverColor, IconColor, IconSize } from '@freelancer/ui/icon';
import { Margin } from '@freelancer/ui/margin';
import { StickyBehaviour, StickyPosition } from '@freelancer/ui/sticky';
import { TextSize } from '@freelancer/ui/text';
import { required } from '@freelancer/ui/validators';
import { ViolationReportContextTypeApi } from 'api-typings/users/users';
import * as Rx from 'rxjs';
import {
  ReportStep,
  ViolationReportReasonText,
  VIOLATION_REASON_TEXT_MAPPING,
} from './violation-report-modal.types';

@Component({
  template: `
    <fl-bit flTrackingSection="ViolationReportModal">
      <ng-container *ngIf="currentReportStep$ | async as currentReportStep">
        <fl-heading
          *ngIf="currentReportStep !== ReportStep.SUCCESS; else successHeader"
          class="ViolationReport-header"
          i18n="Violation report header"
          [size]="TextSize.MID"
          [headingType]="HeadingType.H1"
          [flMarginBottom]="Margin.SMALL"
        >
          Help us understand what's happening
        </fl-heading>

        <ng-template #successHeader>
          <fl-bit
            class="SuccessHeaderContainer"
            [flMarginBottom]="Margin.SMALL"
          >
            <fl-icon
              [name]="'ui-check-in-circle'"
              [size]="IconSize.MID"
              [color]="IconColor.SUCCESS"
              [flMarginRight]="Margin.XXSMALL"
            ></fl-icon>
            <fl-heading
              class="ViolationReport-header"
              i18n="Violation report was sent confirmation text"
              [size]="TextSize.MID"
              [headingType]="HeadingType.H1"
            >
              Thanks for letting us know about this
            </fl-heading>
          </fl-bit>
        </ng-template>

        <fl-bit [flMarginBottom]="Margin.SMALL">
          <app-violation-report-error
            *ngIf="(responsePromise | async)?.status === 'error'"
            [response]="responsePromise | async"
          ></app-violation-report-error>
        </fl-bit>

        <ng-container [ngSwitch]="currentReportStep">
          <ng-container *ngSwitchCase="ReportStep.REASON">
            <fl-heading
              i18n="Violation report reason header"
              [size]="TextSize.SMALL"
              [headingType]="HeadingType.H2"
              [flMarginBottom]="Margin.XXSMALL"
            >
              What's going on?
            </fl-heading>
            <fl-radio
              flTrackingLabel="SelectReportReason"
              [control]="formGroup.get(ReportStep[currentReportStep])"
              [options]="reasonOptions"
            ></fl-radio>
          </ng-container>

          <ng-container *ngSwitchCase="ReportStep.DESCRIPTION">
            <fl-heading
              i18n="Violation report description header"
              [size]="TextSize.SMALL"
              [headingType]="HeadingType.H2"
              [flMarginBottom]="Margin.XXSMALL"
            >
              Please describe the issue
            </fl-heading>
            <fl-textarea
              placeholder="Enter issue description"
              i18n-placeholder="Enter description text"
              flTrackingLabel="EnterIssueDescription"
              [control]="formGroup.get(ReportStep[currentReportStep])"
              [rows]="6"
              [flMarginBottom]="Margin.XSMALL"
            >
            </fl-textarea>
          </ng-container>

          <ng-container *ngSwitchCase="ReportStep.SUCCESS">
            <fl-heading
              i18n="Violation report successful header"
              [size]="TextSize.SMALL"
              [headingType]="HeadingType.H2"
              [flMarginBottom]="Margin.XXSMALL"
            >
              Good news!
            </fl-heading>
            <fl-text i18n="Reporting successful">
              We've received your report and will investigate and take action on
              it shortly.
            </fl-text>
          </ng-container>
        </ng-container>

        <ng-container
          *ngIf="
            formGroup.get(ReportStep[currentReportStep]) as currentFormControl;
            else doneButton
          "
        >
          <fl-bit class="ViolationReport-cta" [flHideMobile]="true">
            <fl-button
              i18n="Cancel button"
              flTrackingLabel="ClickCancelButton"
              [color]="ButtonColor.DEFAULT"
              [size]="ButtonSize.SMALL"
              [flMarginRight]="Margin.SMALL"
              (click)="handleCancelClick()"
            >
              Cancel
            </fl-button>
            <fl-button
              i18n="Continue button"
              flTrackingLabel="ClickContinueButton"
              [color]="ButtonColor.SECONDARY"
              [size]="ButtonSize.SMALL"
              [busy]="responsePromise && (responsePromise | async) === null"
              [disabled]="!currentFormControl.valid"
              (click)="handleContinueClick(currentReportStep)"
            >
              Continue
            </fl-button>
          </fl-bit>

          <fl-container
            class="ViolationReport-cta"
            [flSticky]="true"
            [flStickyBehaviour]="StickyBehaviour.ALWAYS"
            [flStickyPosition]="StickyPosition.BOTTOM"
            [flMarginBottom]="Margin.SMALL"
            [flHideTablet]="true"
            [flHideDesktop]="true"
          >
            <fl-button
              i18n="Cancel button"
              flTrackingLabel="ClickCancelButton"
              [color]="ButtonColor.DEFAULT"
              [size]="ButtonSize.SMALL"
              [flMarginRight]="Margin.SMALL"
              (click)="handleCancelClick()"
            >
              Cancel
            </fl-button>
            <fl-button
              i18n="Continue button"
              flTrackingLabel="ClickContinueButton"
              [color]="ButtonColor.SECONDARY"
              [size]="ButtonSize.SMALL"
              [busy]="responsePromise && (responsePromise | async) === null"
              [disabled]="!currentFormControl.valid"
              (click)="handleContinueClick(currentReportStep)"
            >
              Continue
            </fl-button>
          </fl-container>
        </ng-container>

        <ng-template #doneButton>
          <fl-bit class="ViolationReport-cta" [flHideMobile]="true">
            <fl-button
              *ngIf="currentReportStep === ReportStep.SUCCESS"
              i18n="Done button"
              flTrackingLabel="ClickDoneButton"
              [color]="ButtonColor.SECONDARY"
              [size]="ButtonSize.SMALL"
              (click)="handleDoneClick()"
            >
              Done
            </fl-button>
          </fl-bit>

          <fl-container
            class="ViolationReport-cta"
            [flSticky]="true"
            [flStickyBehaviour]="StickyBehaviour.ALWAYS"
            [flStickyPosition]="StickyPosition.BOTTOM"
            [flMarginBottom]="Margin.SMALL"
            [flHideTablet]="true"
            [flHideDesktop]="true"
          >
            <fl-button
              *ngIf="currentReportStep === ReportStep.SUCCESS"
              i18n="Done button"
              flTrackingLabel="ClickDoneButton"
              [color]="ButtonColor.SECONDARY"
              [size]="ButtonSize.SMALL"
              (click)="handleDoneClick()"
            >
              Done
            </fl-button>
          </fl-container>
        </ng-template>
      </ng-container>
    </fl-bit>
  `,
  styleUrls: ['./violation-report-modal.component.scss'],
})
export class ViolationReportModalComponent implements OnInit {
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  HeadingType = HeadingType;
  HoverColor = HoverColor;
  IconColor = IconColor;
  IconSize = IconSize;
  Margin = Margin;
  ReportStep = ReportStep;
  StickyBehaviour = StickyBehaviour;
  StickyPosition = StickyPosition;
  TextSize = TextSize;

  @Input() contextType: ViolationReportContextTypeApi;
  @Input() contextId: number;
  @Input() violatorUserId: number;

  private currentReportStepSubject$ = new Rx.BehaviorSubject(ReportStep.REASON);
  currentReportStep$ = this.currentReportStepSubject$.asObservable();
  responsePromise: Promise<BackendPushResponse<ViolationReportsCollection>>;

  formGroup = new FormGroup({
    REASON: new FormControl(null, required('You must choose a reason')),
    DESCRIPTION: new FormControl(
      null,
      required('You must provide a description'),
    ),
  });

  reasonOptions: ReadonlyArray<ViolationReportReasonText>;

  constructor(
    private datastore: Datastore,
    private modalRef: ModalRef<ViolationReportModalComponent>,
    private router: Router,
  ) {}

  ngOnInit() {
    if (this.contextType === ViolationReportContextTypeApi.CONTEST_COMMENT) {
      this.reasonOptions = [
        ViolationReportReasonText.PUBLIC_DISPLAY_OF_COMMUNICATION_DETAILS,
        ViolationReportReasonText.INAPPROPRIATE_LANGUAGE,
        ViolationReportReasonText.OTHER,
      ];
    } else if (
      this.contextType === ViolationReportContextTypeApi.CONTEST_ENTRY
    ) {
      this.reasonOptions = [
        ViolationReportReasonText.COPIED_WORK_FROM_USER,
        ViolationReportReasonText.COPIED_WORK_FROM_SOMEONE_ELSE,
        ViolationReportReasonText.WORK_DOES_NOT_MATCH_REQUIREMENTS,
        ViolationReportReasonText.EXPLICIT_OR_INAPPROPRIATE_CONTENT,
        ViolationReportReasonText.LOW_QUALITY_WORK,
        ViolationReportReasonText.OTHER,
      ];
    } else if (this.contextType === ViolationReportContextTypeApi.BID) {
      this.reasonOptions = [
        ViolationReportReasonText.DOES_NOT_MATCH_PROJECT_DESCRIPTION,
        ViolationReportReasonText.DOES_NOT_PROVIDE_ENOUGH_INFORMATION,
        ViolationReportReasonText.CONTAINS_CONTACT_INFORMATION,
        ViolationReportReasonText.OTHER,
      ];
    } else {
      // FIXME: just show all right now but should be edited to support each product
      this.reasonOptions = [
        ViolationReportReasonText.PUBLIC_DISPLAY_OF_COMMUNICATION_DETAILS,
        ViolationReportReasonText.COPIED_WORK_FROM_USER,
        ViolationReportReasonText.COPIED_WORK_FROM_SOMEONE_ELSE,
        ViolationReportReasonText.WORK_DOES_NOT_MATCH_REQUIREMENTS,
        ViolationReportReasonText.EXPLICIT_OR_INAPPROPRIATE_CONTENT,
        ViolationReportReasonText.LOW_QUALITY_WORK,
        ViolationReportReasonText.OTHER,
      ];
    }
  }

  handleCancelClick() {
    this.closeModal();
  }

  handleDoneClick() {
    this.closeModal();
  }

  closeModal() {
    this.modalRef.close();
  }

  handleContinueClick(step: ReportStep) {
    if (step + 1 === ReportStep.SUCCESS) {
      this.submitReport();
    } else {
      this.goToStep(step + 1);
    }
  }

  goToStep(step: ReportStep) {
    this.currentReportStepSubject$.next(step);
  }

  submitReport() {
    const reasonControl: AbstractControl | null = this.formGroup.get(
      ReportStep[ReportStep.REASON],
    );
    if (!reasonControl) {
      throw new Error('No report reason selected');
    }

    const reasonText = reasonControl.value as ViolationReportReasonText;
    const { reason, additionalReason } = VIOLATION_REASON_TEXT_MAPPING[
      reasonText
    ];

    const commentControl = this.formGroup.get(
      ReportStep[ReportStep.DESCRIPTION],
    );
    const comments = commentControl ? (commentControl.value as string) : '';

    this.responsePromise = this.datastore
      .collection<ViolationReportsCollection>('violationReports')
      .push({
        id: -1, // dummy
        contextId: this.contextId,
        contextType: this.contextType,
        reason,
        additionalReason,
        url: this.router.url,
        comments,
        violatorUserId: this.violatorUserId,
      });

    this.responsePromise.then(response =>
      response.status === 'success'
        ? this.goToStep(ReportStep.SUCCESS)
        : undefined,
    );
  }
}
